import { Link, Typography } from '@mui/material';
import { useSubmission } from 'api/submission';
import {
  coverageTableWCMessageVariants,
  META,
  STATES_FULLNAME,
  type TCoverageTableWCMessageVariants,
  WC_STATE_EXCLUSION_MESSAGES,
} from 'common/constants';
import { WarningBanner } from 'components/lib/WarningBanner';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { CoverageTableWCMessageTelLinkStyle } from './CoverageTableWCMessage.styles';

export type CoverageTableWCMessageProps = {
  isMobile?: boolean;
  messageVariant: TCoverageTableWCMessageVariants;
};

const CoverageTableWCMessage: FC<CoverageTableWCMessageProps> = ({
  isMobile = false,
  messageVariant,
}) => {
  const { id } = useParams();
  const { data: submission } = useSubmission(id as string, false);

  if (messageVariant === 'no_message') return <></>;

  const renderMessage = () => {
    switch (messageVariant) {
      case 'with_wc_ex_oo': {
        return (
          <>
            This quote includes Workers&apos; Compensation coverage for employees but
            <Typography component="span" fontWeight="900" mx={0.5}>
              NOT
            </Typography>
            owners/officers. You may elect to add Workers&apos; Compensation coverage for
            owners/officers by calling
          </>
        );
      }

      case 'without_wc': {
        return (
          <>
            This quote
            <Typography component="span" fontWeight="900" mx={0.5}>
              does not
            </Typography>
            include Workers&apos; Compensation coverage because you have indicated you have no
            employees and elected to exclude all owners/officers from coverage. If you&apos;d like
            to discuss adding Worker&apos;s Compensation to your quote please call us at
          </>
        );
      }

      case 'wc_not_available': {
        const wcNotAvailableStateFullName = submission?.us_state;

        const message =
          (wcNotAvailableStateFullName &&
            WC_STATE_EXCLUSION_MESSAGES[wcNotAvailableStateFullName]) ||
          `Workers' Compensation coverage is not available with THREE in ${
            wcNotAvailableStateFullName ? STATES_FULLNAME[wcNotAvailableStateFullName] : ''
          }. For questions, call`;

        return <>{message}</>;
      }

      case 'wc_excluded_by_industry': {
        return 'This class of business is not eligible for Workers’ Compensation coverage with THREE.';
      }

      case 'wc_excluded_by_rule': {
        return "Given the characteristics of this risk, this quote is not eligible for Workers' Compensation coverage with THREE.";
      }

      default: {
        return <></>;
      }
    }
  };

  const showContactNumber =
    messageVariant === coverageTableWCMessageVariants.WITH_WC_EX_OO ||
    messageVariant === coverageTableWCMessageVariants.WITHOUT_WC;

  return (
    <WarningBanner isVisibleIcon={!isMobile}>
      <Typography variant="body2">
        {renderMessage()}

        {showContactNumber && (
          <>
            <Link
              href={`tel:${META.PRIORITY_CONTACT_NUMBER}`}
              sx={CoverageTableWCMessageTelLinkStyle}
            >
              {META.PRIORITY_CONTACT_NUMBER}
            </Link>
            {messageVariant === coverageTableWCMessageVariants.WITH_WC_EX_OO
              ? ', unless otherwise specified by your state’s requirements.'
              : '.'}
          </>
        )}
      </Typography>
    </WarningBanner>
  );
};

export default CoverageTableWCMessage;
